import React, { useState } from "react";
import {
  FaClock,
  FaCalendarCheck,
  FaCheckCircle,
  FaUserCheck,
  FaExclamationTriangle,
  FaBuilding,
} from "react-icons/fa";

const TaskSidebarPanel = ({ tasks, onEdit, currentUsername = "User" }) => {
  const [activeTab, setActiveTab] = useState("today");

  const now = new Date();
  const todayStr = now.toISOString().substring(0, 10);

  // 1. Today's Tasks
  const todaysTasks = tasks.filter((t) => {
    if (!t.due_date) return false;
    return t.due_date.substring(0, 10) === todayStr;
  });

  // 2. Upcoming Deadlines (Due within next 7 days, not completed)
  const upcomingTasks = tasks.filter((t) => {
    if (!t.due_date || t.status === "Completed") return false;
    const dueDate = new Date(t.due_date);
    const diffDays = (dueDate - now) / (1000 * 60 * 60 * 24);
    return diffDays > 0 && diffDays <= 7;
  });

  // 3. Recently Completed (Completed status)
  const recentlyCompleted = tasks
    .filter((t) => t.status === "Completed")
    .slice(0, 10);

  // 4. My Tasks (Assigned to logged-in user or unassigned)
  const myTasks = tasks.filter((t) => {
    if (!t.assigned_to) return false;
    return (
      t.assigned_to.toLowerCase().includes(currentUsername.toLowerCase()) ||
      currentUsername.toLowerCase().includes(t.assigned_to.toLowerCase())
    );
  });

  const getListToRender = () => {
    switch (activeTab) {
      case "today":
        return {
          title: "Today's Tasks",
          icon: <FaClock style={{ color: "#0284c7" }} />,
          list: todaysTasks,
          emptyMsg: "No tasks due today! You're all caught up.",
        };
      case "upcoming":
        return {
          title: "Upcoming Deadlines (7 Days)",
          icon: <FaCalendarCheck style={{ color: "#8b5cf6" }} />,
          list: upcomingTasks,
          emptyMsg: "No approaching deadlines this week.",
        };
      case "completed":
        return {
          title: "Recently Completed",
          icon: <FaCheckCircle style={{ color: "#10b981" }} />,
          list: recentlyCompleted,
          emptyMsg: "No tasks completed yet.",
        };
      case "mytasks":
        return {
          title: `My Assigned Tasks (${currentUsername})`,
          icon: <FaUserCheck style={{ color: "#f59e0b" }} />,
          list: myTasks,
          emptyMsg: "No tasks assigned to your profile.",
        };
      default:
        return { title: "", list: [], emptyMsg: "" };
    }
  };

  const currentSection = getListToRender();

  return (
    <aside className="tm-sidebar-panel">
      {/* Tab Navigation */}
      <div className="tm-panel-tabs">
        <button
          className={`tm-panel-tab ${activeTab === "today" ? "active" : ""}`}
          onClick={() => setActiveTab("today")}
          title="Today's Tasks"
        >
          Today ({todaysTasks.length})
        </button>
        <button
          className={`tm-panel-tab ${activeTab === "upcoming" ? "active" : ""}`}
          onClick={() => setActiveTab("upcoming")}
          title="Upcoming Deadlines"
        >
          Upcoming ({upcomingTasks.length})
        </button>
        <button
          className={`tm-panel-tab ${activeTab === "completed" ? "active" : ""}`}
          onClick={() => setActiveTab("completed")}
          title="Recently Completed"
        >
          Done ({recentlyCompleted.length})
        </button>
        <button
          className={`tm-panel-tab ${activeTab === "mytasks" ? "active" : ""}`}
          onClick={() => setActiveTab("mytasks")}
          title="My Tasks"
        >
          Mine ({myTasks.length})
        </button>
      </div>

      {/* Body List */}
      <div className="tm-panel-body">
        <div className="tm-panel-section-title">
          {currentSection.icon}
          <span>{currentSection.title}</span>
        </div>

        {currentSection.list.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 10px", color: "#94a3b8", fontSize: "13px" }}>
            <p style={{ margin: 0 }}>{currentSection.emptyMsg}</p>
          </div>
        ) : (
          currentSection.list.map((task) => {
            const isTaskOverdue =
              task.due_date &&
              new Date(task.due_date) < now &&
              task.status !== "Completed";

            return (
              <div
                key={task.id}
                className="tm-mini-task"
                onClick={() => onEdit(task)}
              >
                <div className="tm-mini-task-title">{task.title}</div>
                <div className="tm-mini-task-meta">
                  {task.client_name ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <FaBuilding style={{ fontSize: "10px" }} />
                      {task.client_name}
                    </span>
                  ) : (
                    <span>{task.priority || "Normal"}</span>
                  )}

                  <span
                    style={{
                      color: isTaskOverdue ? "#ef4444" : "#64748b",
                      fontWeight: isTaskOverdue ? 700 : 500,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                    }}
                  >
                    {isTaskOverdue && <FaExclamationTriangle />}
                    {task.due_date ? task.due_date.substring(0, 10) : "No due date"}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default TaskSidebarPanel;
